/** The report charts: revenue columns stacked by store (daily and monthly,
 *  the forecast part hatched) and the month's cumulative forecast line. */

import type { ByStore, DayPoint, MonthBar } from '@/api/client'
import { Legend } from '@/ui'
import { cn } from '@/lib/utils'
import { STORES, STORE_FILL, STORE_LABEL, md, sumStore, won } from './format'

export interface Column3 {
  key: string
  label: string
  tick: string
  revenue: ByStore
  /** Expected rest of the month: drawn on top, hatched. */
  forecast?: number
}

const HATCH = 'repeating-linear-gradient(45deg, rgb(var(--chart-1) / 0.55) 0 3px, transparent 3px 6px)'

export function columnsOfDays(days: DayPoint[]): Column3[] {
  return days.map((d) => ({ key: d.date, label: d.date, tick: md(d.date), revenue: d.revenue }))
}

export function columnsOfMonths(bars: MonthBar[]): Column3[] {
  return bars.map((b) => ({ key: b.month, label: b.month, tick: b.month.slice(5) + '월', revenue: b.revenue, forecast: b.forecast_rest }))
}

/** Revenue per bucket, one column each, stacked App Store / Play / AdMob. */
export function RevenueColumns({ columns, label, height = 140, className }: { columns: Column3[]; label: string; height?: number; className?: string }) {
  const totals = columns.map((c) => sumStore(c.revenue) + (c.forecast ?? 0))
  const max = Math.max(1, ...totals)
  const last = columns.length - 1
  const step = columns.length <= 14 ? 1 : columns.length <= 45 ? 7 : 14
  const hasForecast = columns.some((c) => (c.forecast ?? 0) > 0)
  return (
    <figure aria-label={label} className={cn('min-w-0', className)}>
      <div className="flex items-end gap-[2px] border-b border-chart-grid" style={{ height }}>
        {columns.map((c, i) => {
          const parts = STORES.filter((k) => c.revenue[k] > 0)
          const title = `${c.label}  ${won(sumStore(c.revenue))}  (${STORES.map((k) => `${STORE_LABEL[k]} ${won(c.revenue[k])}`).join(', ')})${c.forecast ? `  예상 +${won(c.forecast)}` : ''}`
          return (
            <span key={c.key} title={title} className="flex h-full min-w-0 flex-1 flex-col justify-end hover:bg-muted">
              <span className="mx-auto flex w-full max-w-14 flex-col justify-end" style={{ height: `${(totals[i] / max) * 100}%` }}>
                {(c.forecast ?? 0) > 0 && <span data-series="forecast" className="w-full rounded-t-[3px]" style={{ flexGrow: c.forecast, flexBasis: 0, backgroundImage: HATCH }} />}
                {[...parts].reverse().map((k, j) => (
                  <span key={k} data-series={k} className={cn('w-full', STORE_FILL[k], !c.forecast && j === 0 && 'rounded-t-[3px]')} style={{ flexGrow: c.revenue[k], flexBasis: 0 }} />
                ))}
              </span>
            </span>
          )
        })}
      </div>
      <div aria-hidden className="relative mt-1.5 flex h-4 gap-[2px] text-micro text-muted-foreground">
        {columns.map((c, i) => {
          const show = i === last || (last - i) % step === 0
          return (
            <span key={c.key} className="relative min-w-0 flex-1">
              {show && <span className={cn('absolute whitespace-nowrap tabular-nums', i === 0 ? 'left-0' : i === last ? 'right-0' : 'left-1/2 -translate-x-1/2')}>{c.tick}</span>}
            </span>
          )
        })}
      </div>
      <Legend
        className="mt-2"
        items={[
          ...STORES.map((k) => ({ label: STORE_LABEL[k], swatch: STORE_FILL[k] })),
          ...(hasForecast ? [{ label: '예상', swatch: 'bg-chart-1/40' }] : []),
        ]}
      />
      <figcaption className="mt-1 text-right text-micro tabular-nums text-muted-foreground">최대 {won(max)}</figcaption>
    </figure>
  )
}

/** The month's cumulative revenue: a solid line to the last day with data,
 *  a dashed one to the expected end, and the low-high band around that. */
export function ForecastLine({ cumulative, monthDays, lastDay, mtd, low, mid, high, className }: { cumulative: number[]; monthDays: number; lastDay: number; mtd: number; low: number; mid: number; high: number; className?: string }) {
  const W = 300
  const H = 110
  const pad = 4
  const max = Math.max(1, high, mid, ...cumulative)
  const x = (day: number) => pad + ((day - 1) / Math.max(1, monthDays - 1)) * (W - pad * 2)
  const y = (v: number) => H - pad - (v / max) * (H - pad * 2)
  const actual = cumulative.slice(0, lastDay)
  const path = (pts: [number, number][]) => pts.map(([d, v], i) => `${i ? 'L' : 'M'}${x(d).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const actualPts = actual.map((v, i) => [i + 1, v] as [number, number])
  const startV = actual.length ? actual[actual.length - 1] : mtd
  const dash = lastDay < monthDays ? path([[Math.max(1, lastDay), startV], [monthDays, mid]]) : ''
  const band =
    lastDay < monthDays
      ? `M${x(Math.max(1, lastDay)).toFixed(1)},${y(startV).toFixed(1)} L${x(monthDays).toFixed(1)},${y(high).toFixed(1)} L${x(monthDays).toFixed(1)},${y(low).toFixed(1)} Z`
      : ''
  return (
    <figure aria-label="이번 달 누적 수익과 예상" className={cn('min-w-0', className)}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-36 w-full" role="img" aria-label={`누적 ${won(mtd)}, 월말 예상 ${won(mid)} (${won(low)} ~ ${won(high)})`}>
        <line x1={pad} x2={W - pad} y1={H - pad} y2={H - pad} className="stroke-chart-grid" strokeWidth={1} />
        {band && <path d={band} className="fill-chart-1/15" />}
        {dash && <path d={dash} className="fill-none stroke-chart-1" strokeWidth={1.5} strokeDasharray="4 3" />}
        {actualPts.length > 0 && <path d={path(actualPts)} className="fill-none stroke-chart-1" strokeWidth={2} strokeLinejoin="round" />}
        {lastDay < monthDays && <circle cx={x(monthDays)} cy={y(mid)} r={3} className="fill-chart-1" />}
      </svg>
      <div className="flex justify-between text-micro tabular-nums text-muted-foreground">
        <span>1일</span>
        <span>{monthDays}일</span>
      </div>
    </figure>
  )
}
